/**
 * 3D-View toolbar (snap / measure / trays / conduits / side-panel / fullscreen).
 * Plain JSX — outside <Canvas>.
 */
import { GridFour, Ruler, X, Eye, EyeSlash, ArrowsOutSimple, ArrowsInSimple, Path, Cylinder, Lightning, Lock, LockOpen, Ruler as RulerLine, Question, House, Shield, GridNine } from "@phosphor-icons/react";

export default function R3DToolbar({
  snap, setSnap,
  measureMode, setMeasureMode, measurePicks, clearMeasure,
  showTrays, setShowTrays,
  showConduits, setShowConduits,
  showDimensions, setShowDimensions,
  showShell, setShowShell,
  shellPreset, setShellPreset,
  showWallGrids, setShowWallGrids,
  showWorkingSpace, setShowWorkingSpace,
  showFeederLines, setShowFeederLines,
  onEditOpenings,
  autoValidate, setAutoValidate,
  viewLocked, setViewLocked,
  onHelp,
  sidePanelOpen, setSidePanelOpen,
  isFullscreen, toggleFullscreen,
}) {
  return (
    <div
      className="flex flex-wrap gap-2 items-center mb-3 bg-[#0a0d12] border border-white/10 px-3 py-2"
      data-testid="r3d-toolbar"
    >
      {/* Snap */}
      <div className="flex items-center gap-1 text-xs font-mono text-gray-400">
        <GridFour size={14} /> Snap
      </div>
      <div className="inline-flex border border-white/10 mr-3">
        {[{ v: 0, label: "Off" }, { v: 6, label: '6"' }, { v: 12, label: '12"' }].map((opt) => (
          <button
            key={opt.v}
            onClick={() => setSnap(opt.v)}
            className={`px-2.5 py-1.5 text-xs font-mono ${snap === opt.v ? "bg-[#00E5FF] text-black" : "text-gray-400 hover:text-white"}`}
            data-testid={`r3d-snap-${opt.v}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Measure */}
      <button
        onClick={() => { setMeasureMode(!measureMode); if (measureMode) clearMeasure(); }}
        className={`btn-ghost text-xs py-1.5 inline-flex items-center gap-1 ${measureMode ? "!bg-[#00E5FF] !text-black" : ""}`}
        data-testid="r3d-measure-toggle"
      >
        <Ruler size={14} /> Measure {measureMode ? "(pick 2)" : ""}
      </button>
      {measurePicks.length > 0 && (
        <button onClick={clearMeasure} className="btn-ghost text-xs py-1.5 inline-flex items-center gap-1" data-testid="r3d-measure-clear">
          <X size={12} /> Clear
        </button>
      )}

      {/* Trays toggle */}
      <button
        onClick={() => setShowTrays(!showTrays)}
        className={`btn-ghost text-xs py-1.5 inline-flex items-center gap-1 ${showTrays ? "!bg-[#00E5FF]/20 !text-[#00E5FF] !border-[#00E5FF]/40" : ""}`}
        data-testid="r3d-toggle-trays"
      >
        <Path size={14} /> Trays
      </button>

      {/* Conduits toggle */}
      <button
        onClick={() => setShowConduits(!showConduits)}
        className={`btn-ghost text-xs py-1.5 inline-flex items-center gap-1 ${showConduits ? "!bg-gray-300/20 !text-gray-200 !border-gray-300/40" : ""}`}
        data-testid="r3d-toggle-conduits"
      >
        <Cylinder size={14} /> Conduits
      </button>

      {/* Feeder lines (single-line diagram overlay) */}
      <button
        onClick={() => setShowFeederLines(!showFeederLines)}
        className={`btn-ghost text-xs py-1.5 inline-flex items-center gap-1 ${showFeederLines ? "!bg-[#FFCC00]/20 !text-[#FFCC00] !border-[#FFCC00]/40" : ""}`}
        title="Show dashed lines from every wired load back to its feeder source"
        data-testid="r3d-toggle-feeder-lines"
      >
        <Lightning size={14} weight={showFeederLines ? "fill" : "regular"} /> Feeders
      </button>

      {/* Shell */}
      <button
        onClick={() => setShowShell(!showShell)}
        className={`btn-ghost text-xs py-1.5 inline-flex items-center gap-1 ${showShell ? "!bg-cyan-300/15 !text-cyan-100 !border-cyan-300/40" : ""}`}
        title="Show solid walls + ceiling"
        data-testid="r3d-toggle-shell"
      >
        <House size={14} weight={showShell ? "fill" : "regular"} /> Shell
      </button>

      {/* v2.12 — Shell view presets. Three one-click view bundles: */}
      <div className="inline-flex border border-white/10" data-testid="r3d-shell-presets">
        {[
          { v: "edit",         label: "Edit",         title: "Edit view: Shell OFF — see all equipment, trays and partitions freely. Best for layout work." },
          { v: "structural",   label: "Structural",   title: "Structural view: Shell ON, all 4 walls + roof visible. Best for floor-load and clash review." },
          { v: "presentation", label: "Presentation", title: "Presentation view: Shell ON with the FRONT wall (Wall C) hidden — gives stakeholders a cutaway peek into the room." },
        ].map((p) => (
          <button
            key={p.v}
            onClick={() => setShellPreset && setShellPreset(p.v)}
            className={`px-2.5 py-1.5 text-[11px] font-mono ${shellPreset === p.v
              ? (p.v === "edit"
                  ? "bg-[#FF9F0A]/20 text-[#FF9F0A]"
                  : p.v === "presentation"
                    ? "bg-[#FF66E0]/20 text-[#FF66E0]"
                    : "bg-[#00E5FF]/20 text-[#00E5FF]")
              : "text-gray-400 hover:text-white"}`}
            title={p.title}
            data-testid={`r3d-shell-preset-${p.v}`}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Wall grids */}
      <button
        onClick={() => setShowWallGrids(!showWallGrids)}
        className={`btn-ghost text-xs py-1.5 inline-flex items-center gap-1 ${showWallGrids ? "!bg-cyan-300/15 !text-cyan-100 !border-cyan-300/40" : ""}`}
        title={`Show 12"/60" grids on every wall + ceiling`}
        data-testid="r3d-toggle-wallgrids"
      >
        <GridNine size={14} /> Wall grid
      </button>

      {/* Working space overlay */}
      <button
        onClick={() => setShowWorkingSpace(!showWorkingSpace)}
        className={`btn-ghost text-xs py-1.5 inline-flex items-center gap-1 ${showWorkingSpace ? "!bg-[#FFCC00]/20 !text-[#FFCC00] !border-[#FFCC00]/40" : ""}`}
        title="NEC 110.26(A) working space — yellow zone in front of each panel"
        data-testid="r3d-toggle-workspace"
      >
        <Shield size={14} weight={showWorkingSpace ? "fill" : "regular"} /> WS clearance
      </button>

      {/* Edit openings (only useful when shell is on) */}
      <button
        onClick={onEditOpenings}
        className="btn-ghost text-xs py-1.5 inline-flex items-center gap-1"
        data-testid="r3d-edit-openings"
      >
        Doors/Windows
      </button>

      {/* Auto-validate */}
      <button
        onClick={() => setAutoValidate(!autoValidate)}
        className={`btn-ghost text-xs py-1.5 inline-flex items-center gap-1 ${autoValidate ? "!bg-[#00E5FF]/20 !text-[#00E5FF] !border-[#00E5FF]/40" : ""}`}
        title="Re-run NEC 110.26 check automatically after each drag"
        data-testid="r3d-toggle-auto-validate"
      >
        <Lightning size={14} weight={autoValidate ? "fill" : "regular"} /> Auto-validate
      </button>

      {/* Dimensions */}
      <button
        onClick={() => setShowDimensions(!showDimensions)}
        className={`btn-ghost text-xs py-1.5 inline-flex items-center gap-1 ${showDimensions ? "!bg-[#FFCC00]/20 !text-[#FFCC00] !border-[#FFCC00]/40" : ""}`}
        title="Show distance from each wall when an item is selected"
        data-testid="r3d-toggle-dimensions"
      >
        <RulerLine size={14} /> Dims
      </button>

      {/* Lock view */}
      <button
        onClick={() => setViewLocked(!viewLocked)}
        className={`btn-ghost text-xs py-1.5 inline-flex items-center gap-1 ${viewLocked ? "!bg-red-500/20 !text-red-300 !border-red-500/40" : ""}`}
        title="Disable camera orbit/pan/zoom so equipment moves don't shift the view"
        data-testid="r3d-toggle-lock"
      >
        {viewLocked ? <Lock size={14} weight="fill" /> : <LockOpen size={14} />}
        {viewLocked ? "Locked" : "Lock view"}
      </button>

      {/* Help */}
      <button
        onClick={onHelp}
        className="btn-ghost text-xs py-1.5 inline-flex items-center gap-1"
        title="Workflow guide"
        data-testid="r3d-help"
      >
        <Question size={14} /> Help
      </button>

      {/* Side panel */}
      <button
        onClick={() => setSidePanelOpen(!sidePanelOpen)}
        className="btn-ghost text-xs py-1.5 inline-flex items-center gap-1 ml-auto"
        data-testid="r3d-toggle-side-panel"
      >
        {sidePanelOpen ? <EyeSlash size={14} /> : <Eye size={14} />}
        {sidePanelOpen ? "Hide" : "Show"} Equipment List
      </button>

      {/* Fullscreen */}
      <button
        onClick={toggleFullscreen}
        className="btn-ghost text-xs py-1.5 inline-flex items-center gap-1"
        data-testid="r3d-fullscreen"
      >
        {isFullscreen ? <ArrowsInSimple size={14} /> : <ArrowsOutSimple size={14} />}
        {isFullscreen ? "Exit" : "Fullscreen"}
      </button>
    </div>
  );
}
